import { CopyOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, message, Modal, Radio } from "antd"
import { useContext, useEffect, useState } from "react"
import { PositionsContext } from "../../../context/PositionsContext"
import { useAuth } from "../../../hooks/context/useAuth"
import { useAssetClass } from "../../../hooks/useAssetClass"
import { usePositionsByStructures } from "../../../hooks/usePositionsByStructures"
import { ASSET_LOCALES } from "../../../utils/enums"
import { spaces } from "../../../utils/styles"
import { findPaths, replaceSpacesByUnderlines } from "../../../utils/utils"
import { AssetClassSelect } from "../../atoms/select/AssetClassSelect"
import { GraphViewer } from "../GraphViewer"
import { StructuresAllocationPlot } from "../charts/StructuresAllocationPlot"
import { AssetDistributionByStructureTable } from "../tables/AssetDistributionByStructureTable"

export const AssetInfoModal = ({ asset, date, onClose }) => {
  const [assetClassesOptions, setAssetClassesOptions] = useState([])
  const [assetClass, setAssetClass] = useState(null)
  const [locale, setLocale] = useState(asset.locale)
  const [graphData, setGraphData] = useState([])
  const [positionsByStructures, setPositionsByStructures] = useState([])
  const { updateAssetClass, getAssetClass } = useAssetClass()
  const { forceReload, graph, customersOrFunds, isExplodedView } = useContext(PositionsContext)
  const { isManagementTeam: hasPermissionToEdit } = useAuth()

  const customerSelected = customersOrFunds.length === 1 && customersOrFunds[0]

  const { getPositions } = usePositionsByStructures({ customer: customerSelected, assetId: asset.idAsset, date })

  const buildExplodedGraph = () => {
    const start = customerSelected
    const end = asset.key.toUpperCase()

    const edges = []
    const nodes = []
    const originAlreadyInserted = []

    findPaths(graph, start, end).forEach(({ origin, target, value_brl, value_usd }) => {
      const originId = replaceSpacesByUnderlines(origin)
      const targetId = replaceSpacesByUnderlines(target)

      const insertIfNotExist = (value, id) => {
        if (!originAlreadyInserted.includes(value)) {
          originAlreadyInserted.push(value)
          nodes.push({ data: { id, label: value, bold: [start, end].includes(value) } })
        }
      }

      insertIfNotExist(origin, originId)
      insertIfNotExist(target, targetId)

      edges.push({
        data: {
          source: originId,
          target: targetId,
          value_brl: new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value_brl),
          value_usd: new Intl.NumberFormat("pt-BR", { style: "currency", currency: "USD" }).format(value_usd),
        },
      })
    })

    setGraphData({ nodes, edges, start: replaceSpacesByUnderlines(start), end: replaceSpacesByUnderlines(end) })
  }

  useEffect(() => {
    if (!customerSelected) return
    if (isExplodedView) buildExplodedGraph()
    if (customerSelected) getPositions().then(setPositionsByStructures)
  }, [graph, asset, customersOrFunds])

  useEffect(() => {
    getAssetClass({ locale })
      .then((assetClasses) => {
        setAssetClassesOptions(assetClasses.map((item) => ({ value: item.id, label: item.name })))
      })
      .catch((error) => console.error(error))
  }, [locale])

  useEffect(() => {
    if (assetClassesOptions.length > 0 && asset.idClass && asset.idClass != 74) {
      setAssetClass((assetClassesOptions.find((item) => item.value === asset.idClass) || {}).value)
    }
  }, [assetClassesOptions])

  const handleClose = () => {
    setAssetClass(null)
    setLocale(null)
    onClose()
  }

  const handleOk = () => {
    if (!assetClass || !asset.idAsset) return handleClose()
    updateAssetClass(asset.idAsset, assetClass).then(() => {
      forceReload()
      handleClose()
    })
  }

  return (
    <>
      <Modal
        title={asset?.key}
        open={asset != null}
        onCancel={handleClose}
        maskClosable={true}
        width={1200}
        footer={
          hasPermissionToEdit && [
            <Button key="ok" type="primary" onClick={handleOk}>
              Salvar
            </Button>,
          ]
        }
      >
        <Flex style={{ flexDirection: "column", gap: spaces.space2, paddingTop: spaces.space2, justifyItems: "center" }}>
          <Flex style={{ gap: spaces.space3, justifyContent: "center", alignItems: "center" }}>
            <Flex style={{ alignItems: "center", gap: spaces.space2 }}>
              <b>Locale:</b>
              <Radio.Group onChange={(e) => setLocale(e.target.value)} defaultValue={locale} disabled={!hasPermissionToEdit}>
                <Radio value={ASSET_LOCALES.onshore.value}>Onshore</Radio>
                <Radio value={ASSET_LOCALES.offshore.value}>Offshore</Radio>
              </Radio.Group>
            </Flex>
            <Flex style={{ alignItems: "center", gap: spaces.space2 }}>
              <b>Asset Class:</b>
              <AssetClassSelect
                setAssetClass={setAssetClass}
                value={assetClass}
                disabled={!hasPermissionToEdit || !locale}
                showLabel={false}
                useOnlyParents={false}
                locale={locale}
                width={200}
              />
            </Flex>
            <Flex style={{ alignItems: "center", gap: spaces.space1 }}>
              <b>ISIN:</b>
              <span>{asset?.isin || "No ISIN available"}</span>
              {asset?.isin && (
                <Button
                  style={{ marginLeft: spaces.space2 }}
                  size="small"
                  onClick={async () => {
                    await navigator.clipboard.writeText(asset?.isin)
                    message.success("ISIN copied to clipboard")
                  }}
                  icon={<CopyOutlined />}
                />
              )}
            </Flex>
          </Flex>
          <Flex style={{ width: "100%", justifyContent: "center" }}>
            {isExplodedView && customerSelected && <GraphViewer elements={graphData} />}
            {customerSelected && !isExplodedView && (
              <Flex style={{ flexDirection: "column", gap: spaces.space2, width: "100%" }}>
                <AssetDistributionByStructureTable data={positionsByStructures} />
                <Flex style={{ alignItems: "center", justifyContent: "center" }}>
                  <StructuresAllocationPlot data={positionsByStructures} />
                </Flex>
              </Flex>
            )}
            {!customerSelected && <Empty description="Please select a customer/fund to view the asset distribution or exploded graph." />}
          </Flex>
        </Flex>
      </Modal>
    </>
  )
}
